import { Component, Input, OnInit, forwardRef } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { BehaviorSubject, Subscription, filter } from 'rxjs';
import { cloneDeep } from 'lodash';

import { AbstractFormFieldComponent } from '../../../../PageBuilder/components/FormField/abstract-form-field.component';
import { DropdownOptionModel } from '../../../../Forms/models/dropdown-option.model';
import { InsightsDisplayNameEnum } from '../../../../Mentions/enums/insights-display-name.enum';
import { MentionContentCustomizationEnum } from '../../../../Mentions/enums/mention-content-customization.enum';

import { CurrencyConversionService } from '../../../../../Core/Users/services/currency-conversion.service';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { UserCurrency } from '../../../../../Core/Users/enums/user-currency';

import { MentionCustomizationModel } from '../../../../../Monitor/Shared/models/mention-customization.model';
import { MentionMetadataTypesEnum } from '../../../../../Monitor/Shared/enums/mention-metadata-types.enum';


@Component({
    selector: 'agpr-top-mentions-metadata-field',
    templateUrl: './top-mentions-metadata-field.component.html',
    styleUrls: ['./top-mentions-metadata-field.component.scss'],
    providers: [
        {
            provide: AbstractFormFieldComponent,
            useExisting: forwardRef(() => TopMentionsMetadataFieldComponent)
        }
    ],
    standalone: false
})
export class TopMentionsMetadataFieldComponent extends AbstractFormFieldComponent implements OnInit {

    @Input() declare value: MentionCustomizationModel;
    @Input() id: string = null;

    metadata: FormGroup = new FormGroup<any>({
        highlightKeywords: new FormControl<boolean>(false),
        metadataTypes: new FormGroup<any>({
            mediaType: new FormControl<boolean>(false),
            outlet: new FormControl<boolean>(false),
            author: new FormControl<boolean>(false),
            audienceReach: new FormControl<boolean>(false),
            wordCount: new FormControl<boolean>(false),
            duration: new FormControl<boolean>(false),
            keywords: new FormControl<boolean>(false),
            ave:  new FormControl<boolean>(false),
            publishedDate: new FormControl<boolean>(false),
            domainAuthority: new FormControl<boolean>(false)
        }),
        mentionContent: new FormGroup<any>({
            showSnippet: new FormControl<boolean>(false),
            lead: new FormControl<MentionContentCustomizationEnum>(MentionContentCustomizationEnum.lead),
            summary: new FormControl<MentionContentCustomizationEnum>(MentionContentCustomizationEnum.lead),
        }),
        insights: new FormGroup<any>({
            showInsights: new FormControl<boolean>(false),
            insightsName: new FormControl<InsightsDisplayNameEnum>(InsightsDisplayNameEnum.insightsName),
            custom: new FormControl<InsightsDisplayNameEnum>(InsightsDisplayNameEnum.insightsName),
            customInsightsName: new FormControl<string>(null)
        }),
        currency: new FormControl<UserCurrency>(null),
        timezone: new FormControl<string>(null)

    });


    tempValue: MentionCustomizationModel;

    currencyOptions: DropdownOptionModel[] = [];
    protected currencySuggestions$: BehaviorSubject<DropdownOptionModel[]> = new BehaviorSubject<DropdownOptionModel[]>([]);

    subscriptions: Subscription[] = [];

    readonly mentionContentCustomizations = MentionContentCustomizationEnum;
    readonly insightsDisplayNameTypes = InsightsDisplayNameEnum;

    constructor(
        public currencyConversionService: CurrencyConversionService,
        public currentUserStateService: CurrentUserStateService,
        public localizationService: LocalizationService
    ) {
        super();
    }

    ngOnInit(): void {
        this.currencyOptions = this.currencyConversionService.getCurrencyDropdownOptions();
        this.currencySuggestions$.next(this.currencyOptions);
        this.setFormValues();
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(preferences => {
                this.tempValue.preferredCurrency = preferences.currency;
                this.metadata.get('currency').setValue(this.currencyOptions.find(option => option.value === this.tempValue.preferredCurrency));
                this.tempValue.timezone = preferences.timeZone;
                this.metadata.get('timezone').setValue(this.tempValue.timezone);
                this.changes.emit(this.tempValue);
            }),
            this.mentionContent.get('showSnippet').valueChanges.subscribe(showSnippet => {
                if (showSnippet) {
                    this.tempValue.mentionContent = this.mentionContent.get('lead').value || MentionContentCustomizationEnum.headlineOnly;
                } else {
                    this.tempValue.mentionContent = MentionContentCustomizationEnum.headlineOnly;
                }
            }),
            this.mentionContent.get('lead').valueChanges.subscribe(lead => {
                this.tempValue.mentionContent = lead;
                this.mentionContent.get('summary').setValue(lead, { emitEvent: false });
            }),
            this.mentionContent.get('summary').valueChanges.subscribe(summary => {
                this.tempValue.mentionContent = summary;
                this.mentionContent.get('lead').setValue(summary, { emitEvent: false });
            }),
            this.metadata.get('highlightKeywords').valueChanges.subscribe(highlightKeywords => this.tempValue.highlightKeywords = highlightKeywords),
            this.insights.get('showInsights').valueChanges.subscribe(showInsights => this.tempValue.showInsights = showInsights),
            this.insights.get('insightsName').valueChanges.subscribe(insightsName => {
                this.tempValue.insightsNameDisplay = insightsName;
                this.insights.get('custom').setValue(insightsName, { emitEvent: false });
            }),
            this.insights.get('custom').valueChanges.subscribe(custom => {
                this.tempValue.insightsNameDisplay = custom;
                this.insights.get('insightsName').setValue(custom, { emitEvent: false });
            }),
            this.insights.get('customInsightsName').valueChanges.subscribe(customInsightsName => this.tempValue.customInsightsName = customInsightsName),
            this.metadataTypes.valueChanges.subscribe(metadataTypes => {
                this.tempValue.metadataTypes = (Object.keys(metadataTypes) as MentionMetadataTypesEnum[]).filter(key => metadataTypes[key]);
            }),
            this.metadata.get('currency').valueChanges.subscribe(currency => {
                this.tempValue.preferredCurrency = currency.value;
            }),
            this.metadata.get('timezone').valueChanges.subscribe(timezone => {
                this.tempValue.timezone = timezone;
            }),
            this.metadata.valueChanges.subscribe(metadata => {
                this.changes.emit(this.tempValue);
            })
        );
    }

    setFormValues(): void {
        this.tempValue = cloneDeep(this.value);

        if (!this.tempValue) {
            return;
        }
        this.mentionContent.get('showSnippet').setValue(this.tempValue.mentionContent !== MentionContentCustomizationEnum.headlineOnly);
        this.mentionContent.get('lead').setValue(this.tempValue.mentionContent);
        this.mentionContent.get('summary').setValue(this.tempValue.mentionContent);

        this.metadata.get('highlightKeywords').setValue(this.tempValue.highlightKeywords);

        this.insights.get('showInsights').setValue(this.tempValue.showInsights);
        this.insights.get('insightsName').setValue(this.tempValue.insightsNameDisplay);
        this.insights.get('custom').setValue(this.tempValue.insightsNameDisplay);
        this.insights.get('customInsightsName').setValue(this.tempValue.customInsightsName
            || this.localizationService.transform('Reports.topMentions.metadata.customInsightsName.default'));

        const metadataTypes = this.tempValue.metadataTypes || [];
        this.metadataTypes.get('mediaType').setValue(metadataTypes.indexOf(MentionMetadataTypesEnum.mediaType) > -1);
        this.metadataTypes.get('outlet').setValue(metadataTypes.indexOf(MentionMetadataTypesEnum.outlet) > -1);
        this.metadataTypes.get('author').setValue(metadataTypes.indexOf(MentionMetadataTypesEnum.author) > -1);
        this.metadataTypes.get('audienceReach').setValue(metadataTypes.indexOf(MentionMetadataTypesEnum.audienceReach) > -1);
        this.metadataTypes.get('wordCount').setValue(metadataTypes.indexOf(MentionMetadataTypesEnum.wordCount) > -1);
        this.metadataTypes.get('duration').setValue(metadataTypes.indexOf(MentionMetadataTypesEnum.duration) > -1);
        this.metadataTypes.get('keywords').setValue(metadataTypes.indexOf(MentionMetadataTypesEnum.keywords) > -1);
        this.metadataTypes.get('ave').setValue(metadataTypes.indexOf(MentionMetadataTypesEnum.ave) > -1);
        this.metadata.get('currency').setValue(this.tempValue.preferredCurrency);
        this.metadataTypes.get('publishedDate').setValue(metadataTypes.indexOf(MentionMetadataTypesEnum.publishedDate) > -1);
        this.metadata.get('timezone').setValue(this.tempValue.timezone);
        this.metadataTypes.get('domainAuthority').setValue(metadataTypes.indexOf(MentionMetadataTypesEnum.domainAuthority) > -1);
    }

    get mentionContent(): FormGroup {
        return this.metadata.get('mentionContent') as FormGroup;
    }

    get highlightKeywords(): FormControl {
        return this.metadata.get('highlightKeywords') as FormControl;
    }

    get showSnippet(): boolean {
        return this.mentionContent.get('showSnippet').value;
    }

    get insights(): FormGroup {
        return this.metadata.get('insights') as FormGroup;
    }

    get showInsights(): boolean {
        return this.insights.get('showInsights').value;
    }

    get isCustomInsightsName(): boolean {
        return this.insights.get('custom').value === InsightsDisplayNameEnum.custom;
    }

    get metadataTypes(): FormGroup {
        return this.metadata.get('metadataTypes') as FormGroup;
    }

    get showCurrencySelector(): boolean {
        return this.metadataTypes.get('ave').value;
    }

    get currency(): FormControl {
        return this.metadata.get('currency') as FormControl;
    }

    get showTimezonePicker(): boolean {
        return this.metadataTypes.get('publishedDate').value;
    }

    get timezone(): FormControl {
        return this.metadata.get('timezone') as FormControl;
    }
}
